import _ from 'lodash'
import { LinkValidationPoller } from 'handsontable/utils/linkValidationPoller'
import { LinkManager, cleanLink } from './index'

export class UrlAudits {
  static get() {
    if (!this._instance) {
      this._instance = []
    }

    return this._instance
  }

  static set(newUrlAudits) {
    this._instance = []
    this.add(newUrlAudits)
  }

  static get queue() {
    if (!this._queue) {
      this._queue = []
    }

    return this._queue
  }

  static clearQueue() {
    this._queue = []
  }

  static removeUrl(url) {
    // remove from queue
    this._queue = (this._queue || []).filter(inQueue => {
      return inQueue !== url
    })

    // remove from stored result
    const idxToRemove = this.findIndex(url)
    if (idxToRemove !== -1) {
      this._instance = this.get().filter((value, idx) => {
        return idx !== idxToRemove
      })
    }
  }

  static get patterns() {
    if (!this._patterns) {
      this._patterns = {}
    }

    return this._patterns
  }

  static isUrlInRequestSentQueue(url) {
    return this.queue.includes(url)
  }

  static add(newUrlAudits) {
    if (newUrlAudits) {
      // clone them because we might be augmenting them.
      newUrlAudits = JSON.parse(JSON.stringify(newUrlAudits))

      // add them
      newUrlAudits.forEach(newUrlAudit => {
        newUrlAudit = transformV2Result(newUrlAudit)
        // ensure no duplicates but removing the old one
        // I wish this was stored properly in a key-value map
        this.removeUrl(newUrlAudit.url)
        this.get().push(newUrlAudit)
      })
    }

    return this
  }

  static find(url) {
    if (!url) {
      return []
    }

    const index = this.findIndex(url)
    return index !== -1 ? [this.get()[index]] : []
  }

  static findIndex(url) {
    const urlAudits = this.get()
    const parsed = cleanLink(url)

    return urlAudits.findIndex(urlAudit => {
      if (!urlAudit.parsedUrl) {
        urlAudit.parsedUrl = cleanLink(urlAudit.url)
      }

      // compare a subset of available values.  Ignore the query params
      return (
        parsed.URI.protocol() === urlAudit.parsedUrl.URI.protocol() &&
        parsed.URI.hostname() === urlAudit.parsedUrl.URI.hostname() &&
        (parsed.URI.path() || '').toLowerCase() ===
          (urlAudit.parsedUrl.URI.path() || '').toLowerCase()
      )
    })
  }

  static pollPending(datasetUuid, dataContainer) {
    // if nothing needs updating, don't start polling
    const pending = UrlAudits.get().filter(result => {
      return result.status === 1 || result.status === 2
    })
    if (pending.length === 0) {
      return
    }

    const poller = LinkValidationPoller.getPoller(datasetUuid)
    poller.onResults(results => {
      UrlAudits.add(results)
      dataContainer.forceRender()
    })
    poller.start()
  }

  static cancelPolling(datasetUuid) {
    const poller = LinkValidationPoller.getPoller(datasetUuid)
    poller.cancelPolling()
  }

  static validateUncompleted(dataset, template, dataContainer) {
    const { id, transformedColumns } = template || {}
    const linkField = transformedColumns?.find(
      tc => tc.governance_type == 'link' || tc.field?.governance_type == 'link',
    )
    if (linkField && template?.link_validations?.length) {
      const linkManager = LinkManager.getInstance(dataContainer, UrlAudits, id)
      linkManager.getValidationResultsOnLoad({
        dataset_rows: dataset.dataset_rows,
        linkFieldId: linkField.field_id,
      })
    }
  }

  static getLinkStatusAndRefresh({ dataContainer, templateId, url, preventCache = false }) {
    const urlData = {
      url,
      created: false,
      cvid: null,
      preventCache,
    }
    const linkManager = LinkManager.getInstance(dataContainer, UrlAudits, templateId)
    linkManager.queueValidationResultsOnChange(urlData)
  }
}

function transformV2Result(urlAudit) {
  // we don't have a v2 response body
  if (
    !urlAudit.json_body ||
    !urlAudit.json_body.validationResults ||
    (urlAudit.json_body && urlAudit.json_body.version != 2)
  ) {
    return urlAudit
  }

  const result = urlAudit.json_body
  const { validationResults } = result

  // add a summary to each validation
  validationResults &&
    validationResults.forEach(validation => {
      const checks = validation.checks || []
      const split = _.groupBy(checks, 'success')
      const successes = split.true || []
      const failures = split.false || []
      const unknowns = split.null || []

      const summary = {
        success: successes.length === checks.length,
        successes,
        failures: failures.concat(unknowns),
      }

      validation.summary = summary
    })

  // add a summary to the whole result
  const split = _.groupBy(validationResults, function (validation) {
    return validation.summary.success
  })

  const optionSplit = _.reduce(
    validationResults,
    function (memo, validation) {
      memo.true = memo.true.concat(validation.summary.successes)
      memo.false = memo.false.concat(validation.summary.failures)
      return memo
    },
    { true: [], false: [] },
  )

  result.summary = {
    validationSuccesses: split.true || [],
    validationFailures: split.false || [],
    optionSuccesses: optionSplit.true,
    optionFailures: optionSplit.false,
    totalNumOptions: optionSplit.true.length + optionSplit.false.length,
    success: optionSplit.false.length === 0,
  }

  return urlAudit
}
